import { Shell } from '@/components/Shell';
import { Pill, stateLabel, stateToTone } from '@/components/ui/Pill';
import { type VehicleRow, adminApi } from '@/lib/api';
import { photoUrl } from '@levamar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';

const S3_PUBLIC =
  (import.meta.env.VITE_S3_PUBLIC_URL as string | undefined) ?? 'http://localhost:9000/levamar-dev';

export const Route = createFileRoute('/fleet/')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: FleetPage,
});

type StatusFilter = '' | 'active' | 'maintenance' | 'draft' | 'retired';

function FleetPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'vehicles'],
    queryFn: () => adminApi.vehicles(),
  });

  const setStatus = useMutation({
    mutationFn: (args: { id: string; status: VehicleRow['status'] }) =>
      adminApi.setVehicleStatus(args.id, args.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'vehicles'] }),
  });

  const vehicles = data?.items ?? [];
  const filtered = filter ? vehicles.filter((v) => v.status === filter) : vehicles;

  const counts = {
    all: vehicles.length,
    active: vehicles.filter((v) => v.status === 'active').length,
    maintenance: vehicles.filter((v) => v.status === 'maintenance').length,
    draft: vehicles.filter((v) => v.status === 'draft').length,
  };

  return (
    <Shell page="FROTA" sub={`INVENTÁRIO · ${counts.all} VIATURAS`}>
      {/* Filter + actions row */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 6 }}>
          {(
            [
              { v: '', l: `Todas (${counts.all})` },
              { v: 'active', l: `Activa (${counts.active})` },
              { v: 'maintenance', l: `Manutenção (${counts.maintenance})` },
              { v: 'draft', l: `Rascunho (${counts.draft})` },
            ] as { v: StatusFilter; l: string }[]
          ).map((f) => (
            <button
              key={f.v || 'all'}
              type="button"
              onClick={() => setFilter(f.v)}
              className={`chip${filter === f.v ? ' active' : ''}`}
            >
              {f.l}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: 'auto' }}>
          <Link
            to="/fleet/new"
            style={{
              background: 'var(--lv-brand)',
              color: '#fff',
              padding: '9px 18px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            + NOVA VIATURA
          </Link>
        </div>
      </div>

      {/* Photo card grid */}
      {isLoading ? (
        <div style={{ padding: 32, color: 'var(--lv-gray-500)', fontSize: 13 }}>A carregar…</div>
      ) : filtered.length === 0 ? (
        <div
          style={{
            background: '#fff',
            border: '1px solid var(--lv-line)',
            padding: 32,
            color: 'var(--lv-gray-500)',
            fontSize: 13,
          }}
        >
          Sem viaturas para este filtro.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {filtered.map((v) => (
            <VehicleCard key={v.id} vehicle={v} onStatusChange={setStatus.mutate} />
          ))}
        </div>
      )}
    </Shell>
  );
}

function VehicleCard({
  vehicle,
  onStatusChange,
}: {
  vehicle: VehicleRow;
  onStatusChange: (args: { id: string; status: VehicleRow['status'] }) => void;
}) {
  // Primary photo URL if any photos exist
  const coverUrl = vehicle.coverKey
    ? photoUrl(S3_PUBLIC, { key: vehicle.coverKey, width: vehicle.coverWidth }, 640)
    : null;

  const accentColor = vehicle.defaultColor ?? vehicle.color ?? 'var(--lv-brand)';

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid var(--lv-line)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--lv-brand)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--lv-line)';
      }}
    >
      {/* Photo area */}
      <Link to="/fleet/$id" params={{ id: vehicle.id }} style={{ display: 'block' }}>
        <div
          style={{
            aspectRatio: '16 / 9',
            background: coverUrl ? `url(${coverUrl}) center / cover no-repeat ${accentColor}` : accentColor,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {!coverUrl && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                viewBox="0 0 360 160"
                width={180}
                height={80}
                fill="none"
                opacity={0.35}
                aria-hidden="true"
              >
                <path
                  d="M20 116 L32 84 Q44 64 76 56 L112 38 Q128 24 152 24 L240 24 Q256 24 268 36 L292 60 Q330 64 338 88 L338 120 Z"
                  fill="#fff"
                />
              </svg>
            </div>
          )}

          {/* Status pill overlay */}
          <div style={{ position: 'absolute', top: 8, left: 8 }}>
            <Pill label={stateLabel(vehicle.status)} tone={stateToTone(vehicle.status)} />
          </div>

          {/* Photo count */}
          {vehicle.photoCount > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                padding: '2px 7px',
                letterSpacing: '0.08em',
              }}
            >
              {vehicle.photoCount} FOTO{vehicle.photoCount !== 1 ? 'S' : ''}
            </div>
          )}
        </div>
      </Link>

      {/* Info area */}
      <div style={{ padding: '16px 18px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div>
            <div className="label" style={{ color: 'var(--lv-gray-500)' }}>
              {vehicle.bodyType.toUpperCase()} · {vehicle.year}
            </div>
            <Link
              to="/fleet/$id"
              params={{ id: vehicle.id }}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="display" style={{ fontSize: 20, marginTop: 4, lineHeight: 1.1 }}>
                {vehicle.name}
              </div>
            </Link>
          </div>
        </div>

        {/* Plate */}
        <div
          style={{
            marginTop: 10,
            fontFamily: 'var(--lv-font-mono)',
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--lv-gray-500)',
            letterSpacing: '0.06em',
          }}
        >
          {vehicle.plate}
        </div>

        {/* Pricing */}
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            gap: 16,
            alignItems: 'baseline',
          }}
        >
          <div>
            <span className="display" style={{ fontSize: 26, color: 'var(--lv-brand)' }}>
              {vehicle.eurPerDay.toFixed(0)}
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--lv-gray-500)',
                letterSpacing: '0.08em',
                marginLeft: 3,
              }}
            >
              EUR/DIA
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--lv-gray-500)' }}>{vehicle.cvePerDay.toFixed(0)} CVE</div>
        </div>

        {/* Status changer + edit link */}
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: '1px solid var(--lv-line)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <select
            value={vehicle.status}
            onChange={(e) =>
              onStatusChange({ id: vehicle.id, status: e.target.value as VehicleRow['status'] })
            }
            style={{
              padding: '6px 8px',
              border: '1px solid var(--lv-line)',
              background: '#fff',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              flex: 1,
            }}
          >
            <option value="draft">RASCUNHO</option>
            <option value="active">ACTIVA</option>
            <option value="maintenance">MANUTENÇÃO</option>
            <option value="retired">RETIRADA</option>
          </select>

          <Link
            to="/fleet/$id"
            params={{ id: vehicle.id }}
            style={{
              background: 'transparent',
              border: '1px solid var(--lv-line)',
              color: 'var(--lv-ink)',
              padding: '6px 12px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            EDITAR
          </Link>
        </div>
      </div>
    </div>
  );
}
